/**
 * @file        src/pages/package/HolidayList.jsx
 * @description Domestic & International Holiday tiles. Columns mirror the legacy
 *              screen: # · Image · Title · Status · Entry Date · Action.
 * @author      Kinetika Labs
 * @created     2026-07-01
 * @changelog
 *   - 2026-07-01 Claude — Initial (Package Management: Holiday slice).
 *   - 2026-09-25 Claude — `keepPreviousData: true` is a React Query **v4** option
 *                          and this workspace is on v5 (5.101), where no such
 *                          option exists and it was silently ignored. So every
 *                          page / filter / search change dropped `data` to
 *                          undefined, the table was swapped for six skeleton rows
 *                          and the panel collapsed ~700px before growing back —
 *                          the "pages flicker / the page moves" report. v5 spells
 *                          it `placeholderData: keepPreviousData` (a FUNCTION
 *                          imported from the package, not a boolean).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Select from '../../components/Select';
import Chip from '../../components/Chip';
import Pager from '../../components/Pager';
import TableSkeleton from '../../components/TableSkeleton';
import { get, del } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { toast } from '../../lib/toast';
import { confirm } from '../../lib/confirm';

function fmtDate(s) {
  if (!s) return '—';
  const d = new Date(s);
  return d.toLocaleString('en-IN', {
    hour: '2-digit', minute: '2-digit', hour12: true,
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

export default function HolidayList() {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [filters, setFilters] = useState({ q: '', type: '' });
  const [applied, setApplied] = useState(filters);

  const qc = useQueryClient();
  const params = { ...applied, page, pageSize };
  // Drop empty filter keys so we don't send blank query params.
  Object.keys(params).forEach((k) => params[k] === '' && delete params[k]);

  const { data, isLoading } = useQuery({
    queryKey: qk.holidays.list(params),
    queryFn: () => get('/holidays', { params }),
    placeholderData: keepPreviousData,
  });

  const removeMut = useMutation({
    mutationFn: (id) => del(`/holidays/${id}`),
    onSuccess: () => {
      toast.ok('Deleted', 'Holiday removed.');
      qc.invalidateQueries({ queryKey: ['holidays'] });
    },
    onError: (err) => toast.err('Failed', err.response?.data?.error?.message || 'Could not delete holiday'),
  });

  const rows = data?.data || [];
  const total = data?.meta?.total || 0;

  function applyFilters(e) {
    e.preventDefault();
    setPage(1);
    setApplied(filters);
  }

  async function onDelete(row) {
    if (!(await confirm(`Delete holiday “${row.title}”?`))) return;
    removeMut.mutate(row.id);
  }

  return (
    <>
      <PageHead
        title="Domestic & International Holiday"
        subtitle="Package Management · holiday tiles"
        actions={
          <Link to="/package/holiday/new" className="btn primary"><Icon name="plus" /> Add Holiday</Link>
        }
      />

      <article className="panel glass">
        <form className="search-row" onSubmit={applyFilters}>
          <div className="field">
            <label>Title</label>
            <input placeholder="e.g. Dubai" value={filters.q}
                   onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
          </div>
          <div className="field">
            <label>Type</label>
            <Select
              value={filters.type}
              onChange={(v) => setFilters({ ...filters, type: v })}
              options={[{ value: '', label: 'All' }, { value: 'domestic', label: 'Domestic' }, { value: 'international', label: 'International' }]}
            />
          </div>
          <button className="btn primary" type="submit"><Icon name="search" /> Search</button>
        </form>

        <div className="table-wrap">
          {isLoading ? <TableSkeleton /> : (
            <table className="table">
              <thead>
                <tr><th>#</th><th>Image</th><th>Title</th><th>Type</th><th>Status</th><th>Entry Date</th><th>Action</th></tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={7} className="empty">No holidays yet. Add your first one.</td></tr>
                ) : rows.map((h, idx) => (
                  <tr key={h.id}>
                    <td className="mono">{(page - 1) * pageSize + idx + 1}</td>
                    <td>
                      {h.image
                        ? <img src={h.image} alt={h.title} className="thumb"
                               style={{ width: 72, height: 48, objectFit: 'cover', borderRadius: 6 }} />
                        : <span className="muted">—</span>}
                    </td>
                    <td><b>{h.title}</b></td>
                    <td><Chip tone={h.holiday_type === 'international' ? 'warn' : 'ok'}>
                      {h.holiday_type === 'international' ? 'International' : 'Domestic'}
                    </Chip></td>
                    <td><Chip tone={h.is_active ? 'ok' : 'rej'}>{h.is_active ? 'Active' : 'Inactive'}</Chip></td>
                    <td>{fmtDate(h.created_at)}</td>
                    <td className="row-act">
                      <Link to={`/package/holiday/${h.id}/edit`} className="ib edit" aria-label="Edit"><Icon name="edit" /></Link>
                      <button className="ib del" aria-label="Delete" onClick={() => onDelete(h)}><Icon name="trash" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} />
      </article>
    </>
  );
}
